import apiClient from '../client';

/**
 * Normalises Spring Boot CustomerDto → shape the AuthContext expects:
 *   { id, email, name, phone, avatar, emailVerified }
 */
const normaliseUser = (customer) => ({
  id: String(customer?.id ?? ''),
  email: customer?.email ?? '',
  name: [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || customer?.email,
  phone: customer?.phone ?? '',
  avatar: null,
  emailVerified: customer?.emailVerified ?? false,
});

// ── Registration — Step 1: creates account, sends OTP ────────────────────────
// Returns: { success, email, expiresInMinutes }
export const register = async (name, email, password) => {
  const parts     = name.trim().split(' ');
  const firstName = parts[0];
  const lastName  = parts.slice(1).join(' ') || '';
  const { data }  = await apiClient.post('/auth/register', {
    firstName,
    lastName,
    email,
    password,
    // phone intentionally omitted — optional, not collected on signup
  });
  return data; // { success, message, email, expiresInMinutes }
};

// ── Registration — Step 2: verify OTP → get JWT ───────────────────────────────
// Returns: normalised user object (also persists token to localStorage)
export const verifyEmail = async (email, otp) => {
  const { data } = await apiClient.post('/auth/verify-email', { email, otp });
  const user = normaliseUser(data.customer);
  localStorage.setItem('skinnthrive_token', data.accessToken);
  if (data.refreshToken) localStorage.setItem('skinnthrive_refresh_token', data.refreshToken);
  localStorage.setItem('skinnthrive_user', JSON.stringify(user));
  return user;
};

// ── Resend registration OTP ───────────────────────────────────────────────────
export const resendOtp = async (email) => {
  const { data } = await apiClient.post('/auth/resend-otp', {
    email,
    purpose: 'REGISTRATION',
  });
  return data; // { success, message, email, expiresInMinutes }
};

// ── Login ─────────────────────────────────────────────────────────────────────
// On EMAIL_NOT_VERIFIED the backend returns errorCode + email in the error body.
// We re-throw an enriched error so AuthContext can set pending verification state.
export const login = async (email, password) => {
  try {
    const { data } = await apiClient.post('/auth/login', { email, password });
    const user = normaliseUser(data.customer);
    localStorage.setItem('skinnthrive_token', data.accessToken);
    if (data.refreshToken) localStorage.setItem('skinnthrive_refresh_token', data.refreshToken);
    localStorage.setItem('skinnthrive_user', JSON.stringify(user));
    return user;
  } catch (err) {
    const errData = err?.response?.data;
    if (errData?.errorCode === 'EMAIL_NOT_VERIFIED') {
      const e = new Error(errData.message || 'Email not verified');
      e.errorCode = 'EMAIL_NOT_VERIFIED';
      e.email     = errData.email || email;
      throw e;
    }
    const msg = errData?.message || err?.message || 'Invalid email or password';
    throw new Error(msg);
  }
};

// ── Logout ────────────────────────────────────────────────────────────────────
export const logout = async () => {
  try {
    // send the refresh token too, so the server can revoke the whole session (not just the 1-hour access token)
    await apiClient.post('/auth/logout', { refreshToken: localStorage.getItem('skinnthrive_refresh_token') });
  } catch {
    // best-effort; always clear local storage
  } finally {
    localStorage.removeItem('skinnthrive_token');
    localStorage.removeItem('skinnthrive_refresh_token');
    localStorage.removeItem('skinnthrive_user');
  }
};

// ── Forgot Password — Step 1: send reset OTP ─────────────────────────────────
export const forgotPassword = async (email) => {
  const { data } = await apiClient.post('/auth/forgot-password', { email });
  return data; // { success, message, email, expiresInMinutes }
};

// ── Reset Password — Step 2: verify OTP + set new password ───────────────────
export const resetPassword = async (email, otp, newPassword) => {
  const { data } = await apiClient.post('/auth/reset-password', { email, otp, newPassword });
  return data; // { success, message }
};

// ── Orders ────────────────────────────────────────────────────────────────────
const FULFILLMENT_DISPLAY = {
  UNFULFILLED: 'Unfulfilled', PROCESSING: 'Processing',
  SHIPPED: 'Shipped', DELIVERED: 'Delivered', CANCELLED: 'Cancelled',
};

const normaliseOrder = (o) => {
  const addr = o.shippingAddress;
  return {
    ...o,
    id: o.orderNumber || String(o.id),
    status: FULFILLMENT_DISPLAY[o.fulfillmentStatus] || o.fulfillmentStatus || 'Processing',
    subtotal: Number(o.subtotal || 0),
    discount: Number(o.discountAmount || 0),
    shipping: Number(o.shippingAmount || 0),
    total: Number(o.totalAmount || 0),
    address: addr ? {
      name: addr.name,
      phone: addr.phone,
      line1: addr.addressLine1,
      line2: addr.addressLine2,
      city: addr.city,
      state: addr.state,
      pincode: addr.pincode,
    } : null,
    tracking: o.shipment
      ? { awb: o.shipment.awbCode, courier: o.shipment.courier, status: o.shipment.status }
      : null,
  };
};

// ── Profile ──────────────────────────────────────────────────────────────────
export const updateProfile = async ({ firstName, lastName, phone }) => {
  const { data } = await apiClient.put('/account/profile', { firstName, lastName, phone });
  return data;
};

// ── Addresses ────────────────────────────────────────────────────────────────
export const getAddresses = async () => {
  const { data } = await apiClient.get('/account/addresses');
  return Array.isArray(data) ? data : data?.content ?? [];
};

export const addAddress = async (address) => {
  const { data } = await apiClient.post('/account/addresses', address);
  return data;
};

export const deleteAddress = async (addressId) => {
  await apiClient.delete(`/account/addresses/${addressId}`);
};

// ── Orders ────────────────────────────────────────────────────────────────────
export const getOrders = async () => {
  const { data } = await apiClient.get('/orders');
  const list = data?.content ?? data ?? [];
  return list.map(normaliseOrder);
};
